import type { AssetFiltersState } from "@/store/v2/assetFilters/types";

import type { Asset } from "../types";

/**
 * Checks if all filters are inactive.
 */
export function areAllFiltersInactive(filters: Partial<AssetFiltersState["filters"]>) {
  const { searchText, tags, types, categories } = filters || {};
  return (
    !isFilterPropertyValid(tags) && !isFilterPropertyValid(types) && !isFilterPropertyValid(categories) && !searchText
  );
}

/**
 * Filters the list of assets based on the parameters.
 */
export function filterAssets(assets: Asset[], filters: Partial<AssetFiltersState["filters"]>) {
  const { searchText, tags, types, categories } = filters || {};

  let data = assets;

  if (isFilterPropertyValid(tags)) {
    data = data.filter((asset) => doTagsMatch(asset, tags!));
  }
  if (isFilterPropertyValid(types)) {
    data = data.filter((asset) => doTypesMatch(asset, types!));
  }
  if (isFilterPropertyValid(categories)) {
    data = data.filter((asset) => doCategoriesMatch(asset, categories!));
  }
  if (searchText) {
    data = data.filter((asset) => doesSearchTextMatch(asset, searchText));
  }

  return data;
}

/**
 * Checks if a filter property is valid.
 * A filter property will be valid when it contains at least one key.
 */
export function isFilterPropertyValid(property?: Record<number, boolean>) {
  return Object.keys(property || {}).length > 0;
}

/**
 * Identifies if the asset matches the tags filter.
 */
export function doTagsMatch(asset: Asset, tags: Record<number, boolean>) {
  return asset.mediaItemTags.some((tag) => tags[tag.id]);
}

/**
 * Identifies if the asset matches the types filter.
 */
export function doTypesMatch(asset: Asset, types: Record<string, boolean>) {
  return !!types[asset.contentType.name];
}

/**
 * Identifies if the asset matches the categories filter.
 */
export function doCategoriesMatch(asset: Asset, categories: Record<number, boolean>) {
  return !!categories[asset.assetCategoryId!] || (categories[0] && !asset.assetCategoryId);
}

/**
 * Identifies if the asset matches the search text.
 */
export function doesSearchTextMatch(asset: Asset, searchText: string) {
  const text = [asset.name, JSON.stringify(asset.mediaItemTags), asset.link, asset.contentType?.value]
    .join(" ")
    .toLowerCase();
  return searchText
    .toLowerCase()
    .split(/\s+/)
    .filter((str) => str.length >= 1)
    .every((part) => text.includes(part));
}
